import React, { useState, useEffect, forwardRef } from 'react';
import { tr } from '../../i18n/i18n';
import { useTranslation } from 'react-i18next';
import { InputNumber } from 'primereact/inputnumber';
import { InputTextarea } from 'primereact/inputtextarea';
import { AppButton } from '../atoms';
import { PrimaryModal, ModalHandle } from '../Modals';
import dayjs from 'dayjs';
import { useOrgCurrency } from '../../hooks/useOrgCurrency';

interface StartSessionModalProps {
  onSubmit: (data: { openingBalance: number; notes: string }) => void;
}

interface FormData {
  openingBalance: number | null;
  notes: string;
}

interface FormErrors {
  openingBalance?: string;
}

/**
 * Start Cashier Session Modal - Phase 2
 *
 * Purpose: Form to collect opening balance and notes
 * Opens after SessionBlockingModal, before SessionConfirmationModal
 *
 * Flow: SessionBlockingModal → StartSessionModal → SessionConfirmationModal → API Call
 *
 * Epic: CVA-2286
 *
 * Pattern: Uses PrimaryModal with forwardRef (matches ConfirmationModal standard)
 */
const StartSessionModal = forwardRef<ModalHandle, StartSessionModalProps>(
  ({ onSubmit }, ref) => {
  const { t } = useTranslation();
  // Get organization currency using hook (set during login in localStorage)
  const orgCurrency = useOrgCurrency();

  // Current time and date for Session Details box (displayed BEFORE starting session)
  const currentTime = dayjs().format('hh:mm A'); // "01:33 PM"
  const currentDate = dayjs().format('MMM D, YYYY'); // "Dec 3, 2025"

  const [formData, setFormData] = useState<FormData>({
    openingBalance: null,
    notes: '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  
  // Track if input is valid (value > 0)
  const [isValidInput, setIsValidInput] = useState(false);
  
  // Track real-time validation message
  const [realtimeError, setRealtimeError] = useState<string>('');

  // Helper function to validate raw input value
  const validateRawInput = (rawValue: string): boolean => {
    if (!rawValue || rawValue.trim() === '') {
      setRealtimeError('');
      return false;
    }
    
    // Remove currency symbols and commas for validation
    const cleanValue = rawValue.replace(/[^0-9.-]/g, '');
    const numValue = parseFloat(cleanValue);
    
    if (isNaN(numValue)) {
      setRealtimeError(tr('g.please_enter_a_valid_number', 'Please enter a valid number'));
      return false;
    }
    
    if (numValue <= 0) {
      setRealtimeError(tr('g.opening_balance_must_be_greater_than_zero', 'Opening balance must be greater than zero'));
      return false;
    }
    
    setRealtimeError('');
    return true;
  };

  // Reset form helper
  const resetForm = () => {
    setFormData({ openingBalance: null, notes: '' });
    setErrors({});
    setIsValidInput(false);
    setRealtimeError('');
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (formData.openingBalance === null || formData.openingBalance <= 0) {
      newErrors.openingBalance = tr('g.opening_balance_must_be_greater_than_zero', 'Opening balance must be greater than zero');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission - pass data to parent to open confirmation modal
  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    onSubmit({
      openingBalance: formData.openingBalance!,
      notes: formData.notes,
    });

    // Close modal after successful submission
    resetForm();
    if (ref && typeof ref !== 'function' && ref.current) {
      ref.current.closeModal();
    }
  };

  // Handle cancel - close modal and reset form
  const handleCancel = () => {
    resetForm();
    if (ref && typeof ref !== 'function' && ref.current) {
      ref.current.closeModal();
    }
  };

  return (
    <PrimaryModal
      ref={ref}
      width="530px"
      showHeader={false}
      onClose={handleCancel}
      content={
        <div className="flex flex-col gap-4">
          {/* Modal Header */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#dcfce7] flex items-center justify-center shrink-0">
              <i className="pi pi-play-circle text-green-600 text-2xl" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xl font-semibold text-gray-900">{t('payment_session.start_session', 'Start Cashier Session')}</span>
              <span className="text-sm text-gray-600">{t('g.begin_your_shift_by_entering_the_opening_cash_balance', 'Begin your shift by entering the opening cash balance')}</span>
            </div>
          </div>
        {/* Important Alert Box (Blue background, exact Figma styling) */}
        <div className="flex gap-3 p-4 rounded-md border-[1.6px] bg-[#eff6ff] border-[#bedbff]">
          {/* Info icon (20px) */}
          <i className="pi pi-info-circle text-[20px] text-[#1c398e]" />

          <div className="flex flex-col gap-1">
            {/* "Important" heading (15px, SemiBold, #1c398e) */}
            <p className="text-[15px] font-semibold m-0 leading-tight text-[#1c398e]">{t('common.important', 'Important')}</p>

            {/* Explanation text (13px, #193cb8) */}
            <p className="text-[13px] m-0 leading-relaxed text-[#193cb8]">
              {t(
                'payment_session.opening_balance_instructions',
                'Count all physical cash in your drawer before starting. This opening balance will be used to calculate your cash variance at the end of your shift.',
              )}
            </p>
          </div>
        </div>

        {/* Opening Cash Balance Field */}
        <div className="flex flex-col gap-2">
          <label htmlFor="openingBalance" className="text-[12px] font-semibold text-[#374151]">
            {t('payment_session.opening_cash_balance', 'Opening Cash Balance ({{currency}})', { currency: orgCurrency })}{' '}
            <span className="text-red-500">*</span>
          </label>

          <InputNumber
            id="openingBalance"
            value={formData.openingBalance}
            onChange={(e) => {
              // Validate input immediately (fixes CVA-3593 - must be > 0)
              const rawValue = (e.originalEvent?.target as HTMLInputElement)?.value || '';
              setIsValidInput(validateRawInput(rawValue));
            }}
            onKeyUp={(e: any) => {
              // Additional validation via keyboard events for immediate feedback
              const inputValue = e.target?.value || '';
              setIsValidInput(validateRawInput(inputValue));
            }}
            onValueChange={(e) => {
              setFormData({ ...formData, openingBalance: e.value });
              if (errors.openingBalance) {
                setErrors({ ...errors, openingBalance: undefined });
              }
              // Clear realtime error when valid value is set
              if (e.value !== null && e.value > 0) {
                setRealtimeError('');
              }
            }}
            mode="currency"
            currency={orgCurrency}
            locale="en-US"
            minFractionDigits={2}
            maxFractionDigits={2}
            min={0.01}
            placeholder={t('payment_session.enter_opening_balance_placeholder', 'Enter opening balance (e.g., 5000.00)')}
            className={`w-full h-[36px] border-[#d1d5db] rounded-[5px] ${
              (errors.openingBalance || realtimeError) ? 'p-invalid' : ''
            }`}
            autoFocus
          />

          {(realtimeError || errors.openingBalance) && (
            <small className="text-red-500">{realtimeError || errors.openingBalance}</small>
          )}

          <small className="text-[12px] text-[#4a5565]">{t('g.this_is_the_physical_float_in_your_cash_drawer_right_no', 'This is the physical float in your cash drawer right now')}</small>
        </div>

        {/* Notes Field (Optional) */}
        {/* <div className="flex flex-col gap-2">
          <label htmlFor="notes" className="text-[12px] font-semibold text-[#374151]">
            Notes <span className="text-gray-400">(Optional)</span>
          </label>

          <InputTextarea
            id="notes"
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            rows={3}
            placeholder={t('common.placeholder_session_notes', 'Add any notes about this session (e.g., holidays, special circumstances)')}
            className="w-full border-[#d1d5db] rounded-[5px]"
            maxLength={500}
          />

          <small className="text-[12px] text-[#4a5565]">
            {formData.notes.length}/500 characters
          </small>
        </div> */}

        {/* Session Details Box (Gray background, shows BEFORE starting session) */}
        <div className="flex flex-col gap-3 p-4 rounded-md bg-[#f9fafb]">
          <p className="text-[16px] font-semibold text-[#101828] m-0">{t('common.session_details', 'Session Details')}</p>

          <div className="flex justify-between items-center">
            {/* Left: Session Start Time */}
            <div className="flex flex-col gap-1">
              <p className="text-[13px] text-gray-600 m-0">{t('common.session_start_time', 'Session Start Time')}</p>
              <p className="text-[15px] font-medium text-gray-900 m-0">{currentTime}</p>
            </div>

            {/* Right: Date */}
            <div className="flex flex-col gap-1 text-right">
              <p className="text-[13px] text-gray-600 m-0">{t('common.label_date', 'Date')}</p>
              <p className="text-[15px] font-medium text-gray-900 m-0">{currentDate}</p>
            </div>
          </div>
        </div>

          {/* Action Buttons (Full width, side by side) */}
          <div className="flex gap-2 w-full mt-2">
            <AppButton
              label={t('common.button_cancel')}
              outlined
              onClick={handleCancel}
              className="flex-1 border-[#6c4e9a] text-[#6c4e9a] py-[11px] px-[12px] pb-[21px]"
            />
            <AppButton
              label={t('common.button_next', 'Next')}
              onClick={handleSubmit}
              disabled={!isValidInput}
              className="flex-1 bg-[#6c4e9a] border-[#6c4e9a] text-white py-[11px] px-[12px] pb-[20px]"
            />
          </div>
        </div>
      }
    />
  );
},
);

StartSessionModal.displayName = 'StartSessionModal';

export default StartSessionModal;
